import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaUserClock } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Col, Container, Input, Row, Badge } from "reactstrap";
import { shiftActions } from "../../sagas/shiftSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { formatPrice } from "../../helpers/utility";
import Swal from "sweetalert2";

const ShiftDetails = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const location = useLocation();
    const editMode = location.state?.editMode;
    const shift = location.state?.shift;

    const { summary, loading } = useSelector((state) => state.shift);
    const { list } = useSelector((state) => state.deliveryNotes);

    const [closingCash, setClosingCash] = useState(0);
    const [closingCard, setClosingCard] = useState(0);

    const isValidated = summary?.status === 'VALIDATED';
    const isClosed = summary?.status === "CLOSED";

    const [filters, setFilters] = useState({
        clientId: null,
        shiftId: null,
        commercialId: null,
        dateFrom: null,
        dateTo: null,
        query: ''
    });

    useEffect(() => {
        if (shift?.id) {
            dispatch(shiftActions.fetchSummary({ shiftId: shift.id }));
            dispatch(deliveryActions.list({ ...filters, shiftId: shift.id }));
        }
    }, [shift?.id, dispatch]);

    useEffect(() => {
        if (summary) {
            setClosingCash(summary.closingAmountCash || 0);
            setClosingCard(summary.closingAmountCard || 0);
        }
    }, [summary]);

    const cashDifference = useMemo(() => Number(closingCash) - Number(summary?.expectedCashAmount || 0), [closingCash, summary]);
    const cardDifference = useMemo(() => Number(closingCard) - Number(summary?.expectedCardAmount || 0), [closingCard, summary]);

    const handleValidate = () => {
        Swal.fire({
            title: "Valider la clôture ?",
            text: "Cette action est définitive. Voulez-vous continuer ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, valider",
            cancelButtonText: "Annuler",
            reverseButtons: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33"
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(
                    shiftActions.validateShift({
                        id: summary.shiftId,
                        data: {
                            isValidated: true,
                            closingAmountCash: closingCash,
                            closingAmountCard: closingCard
                        },
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('common.updated'),
                                text: t('shifts.updatedSuccess'),
                            });
                            navigate(-1)
                        }
                    })
                );
            }
        });
    };


    if (!summary) return null;

    return (
        <Container fluid className="py-3">
            {/* Header */}
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="d-flex justify-content-between align-items-center py-2 px-3">
                            <h4 className="mb-0 d-flex align-items-center text-primary">
                                <FaUserClock className="me-2" />
                                {t("shifts.details")} {shift.shiftCode}
                            </h4>
                            <Badge
                                color={
                                    isValidated ? "success" :
                                        isClosed ? "secondary" :
                                            "warning"
                                }
                                pill
                                className="ms-3"
                            >
                                {isValidated
                                    ? t("shifts.validated")
                                    : isClosed
                                        ? t("shifts.closed")
                                        : t("shifts.opened")}
                            </Badge>
                            <Button color="secondary" size="sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                {t("common.return")}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Shift Info */}
            <Row className="mb-3 g-3">
                <Col md={4}>
                    <Card className="shadow-sm p-2 text-center">
                        <div><strong>{t("shifts.caissier")}</strong></div>
                        <div>{summary.commercialName}</div>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm p-2 text-center">
                        <div><strong>{t("shifts.openingTime")}</strong></div>
                        <div>{new Date(summary.openingTime).toLocaleString()}</div>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm p-2 text-center">
                        <div><strong>{t("shifts.closingTime")}</strong></div>
                        <div>{summary.closingTime ? new Date(summary.closingTime).toLocaleString() : "-"}</div>
                    </Card>
                </Col>
            </Row>

            {/* Cash & Card Summary */}
            <Row className="mb-3 g-3">
                <Col md={6}>
                    <Card className="shadow-sm p-3">
                        <h6 className="text-muted mb-2">{t("shifts.cashSummary")}</h6>
                        <Row className="align-items-center mb-2">
                            <Col xs={6}>{t("shifts.expectedCash")}:</Col>
                            <Col xs={6} className="fw-bold">{formatPrice(summary.expectedCashAmount)}</Col>
                        </Row>
                        <Row className="align-items-center mb-2">
                            <Col xs={6}>{t("shifts.closingAmountCash")}:</Col>
                            <Col xs={6}>
                                <Input
                                    type="number"
                                    disabled={!editMode || !isClosed || isValidated}
                                    value={closingCash}
                                    onChange={(e) => setClosingCash(e.target.value)}
                                    bsSize="sm"
                                />
                            </Col>
                        </Row>
                        <Row className="align-items-center">
                            <Col xs={6}>{t("shifts.cashDifference")}:</Col>
                            <Col xs={6}>
                                <Badge color={cashDifference === 0 ? "success" : "danger"}>{formatPrice(cashDifference)}</Badge>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="shadow-sm p-3">
                        <h6 className="text-muted mb-2">{t("shifts.cardSummary")}</h6>
                        <Row className="align-items-center mb-2">
                            <Col xs={6}>{t("shifts.expectedCard")}:</Col>
                            <Col xs={6} className="fw-bold">{formatPrice(summary.expectedCardAmount)}</Col>
                        </Row>
                        <Row className="align-items-center mb-2">
                            <Col xs={6}>{t("shifts.closingAmountCard")}:</Col>
                            <Col xs={6}>
                                <Input
                                    type="number"
                                    disabled={!editMode || !isClosed || isValidated}
                                    value={closingCard}
                                    onChange={(e) => setClosingCard(e.target.value)}
                                    bsSize="sm"
                                />
                            </Col>
                        </Row>
                        <Row className="align-items-center">
                            <Col xs={6}>{t("shifts.cardDifference")}:</Col>
                            <Col xs={6}>
                                <Badge color={cardDifference === 0 ? "success" : "danger"}>{formatPrice(cardDifference)}</Badge>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* Validate Button */}
            {editMode && isClosed && !isValidated && (
                <Row className="mb-3">
                    <Col className="text-end">
                        <Button
                            color="success"
                            onClick={handleValidate}
                            disabled={loading}
                        >
                            {t("common.validate")}
                        </Button>
                    </Col>
                </Row>
            )}

            {/* Delivery Notes Table */}
            <Card className="shadow-sm">
                <CardBody className="p-2">
                    <table className="table table-hover table-striped table-bordered mb-0">
                        <thead className="thead-light">
                            <tr className="text-center">
                                <th>{t("delivery.numero")}</th>
                                <th>{t("commande.client")}</th>
                                <th>{t("commande.dateCreation")}</th>
                                <th>{t("delivery.commercial")}</th>
                                <th>{t("devis.montantTTC")}</th>
                                <th>{t("reglement.montantPaye")}</th>
                                <th>{t("delivery.status")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.length ? (
                                list.map((bl) => (
                                    <tr key={bl.id} className="text-center align-middle">
                                        <td>{bl.numero}</td>
                                        <td>{bl.clientNom || "N/A"}</td>
                                        <td>{bl.createdAt || "N/A"}</td>
                                        <td>{bl.commercialNom || "LOCAL"}</td>
                                        <td>{formatPrice(bl.montantTotalTTC, false)}</td>
                                        <td>{formatPrice(bl.montantPaye, false)}</td>
                                        <td>
                                            <Badge
                                                color={
                                                    bl.status === "EN_PREPARATION" ? "warning" :
                                                        bl.status === "EN_COURS" ? "secondary" :
                                                            bl.status === "LIVRE" ? "success" :
                                                                bl.status === "RETOUR" ? "danger" : "light"
                                                }
                                            >
                                                {t(`delivery.${bl.status.toLowerCase()}`)}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="text-center py-4 text-muted">{t("delivery.noBL")}</td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="bg-light fw-bold">
                                <td colSpan="4" className="text-end">{t('common.totals')}</td>
                                <td className="text-center">{formatPrice(list.reduce((sum, bl) => sum + (bl.montantTotalTTC || 0), 0), false)}</td>
                                <td className="text-center">{formatPrice(list.reduce((sum, bl) => sum + (bl.montantPaye || 0), 0), false)}</td>
                                <td colSpan="1"></td>
                            </tr>
                        </tfoot>
                    </table>
                </CardBody>
            </Card>
        </Container>
    );
};

export default ShiftDetails;
